import { Injectable, Type } from "@angular/core";
import { DialogService } from "common/dialog/dialog.service";
import { PreferencesService } from "core/preferences.service";
import { LanguageService } from "core/translate/language.service";
import MaintenancePushNotification = stages.core.maintenance.MaintenancePushNotification;
import MaintenanceMode = stages.core.maintenance.MaintenanceMode;

export interface MaintenanceNotification {
	lastNotified: string | null;
	maintenanceId: string | null;
	doNotShowAgain: boolean;
	mode: MaintenanceMode;
}

@Injectable({ providedIn: "root" })
export class MaintenanceService {
	private MAINTENANCE_NOTIFICATION = "maintenance.notification";

	constructor(
		private dialogService: DialogService,
		private preferencesService: PreferencesService,
		private languageService: LanguageService,
	) {}

	async notify(
		titleKey: string,
		messageKey: string,
		maintenanceInfo: MaintenancePushNotification,
		maintenanceMode: MaintenanceMode,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- We can not use MaintenanceComponent directly, because it would result in a circular dependency error
		confirmMaintenanceComponentType: Type<any>,
		force: boolean = false,
	): Promise<void> {
		if ((await this.isNotified(maintenanceInfo)) && !force) {
			return;
		}

		const result = await this.dialogService.open("maintenance", {
			component: confirmMaintenanceComponentType,
			componentArgs: {
				titleKey: titleKey,
				messageKey: messageKey,
				maintenanceInfo: maintenanceInfo,
			},
			defaultResult: { action: "cancel", doNotShowAgain: false },
			dialogConfig: { size: "medium", autoHeight: true },
		});

		if (result.action === "confirm") {
			this.preferencesService.setPreference(this.MAINTENANCE_NOTIFICATION, {
				lastNotified: new Date().toJSON(),
				maintenanceId: maintenanceInfo.maintenanceId,
				doNotShowAgain: result.doNotShowAgain,
				mode: maintenanceMode,
			} as MaintenanceNotification);
		}
	}

	private async isNotified(maintenanceInfo: MaintenancePushNotification): Promise<boolean> {
		const maintenanceNotification = await this.preferencesService.getGlobalPreference(this.MAINTENANCE_NOTIFICATION, {
			lastNotified: null,
			maintenanceId: null,
			doNotShowAgain: false,
			mode: "ANNOUNCED",
		} as MaintenanceNotification);

		if (maintenanceNotification.maintenanceId !== maintenanceInfo.maintenanceId) {
			return false;
		}

		if (maintenanceNotification.mode !== maintenanceInfo.maintenanceMode) {
			return false;
		}

		if (maintenanceNotification.doNotShowAgain) {
			return true;
		}

		if (maintenanceNotification.lastNotified === null) {
			return false;
		}

		return this.datesAreOnSameDay(new Date(maintenanceNotification.lastNotified), new Date());
	}

	private datesAreOnSameDay(first: Date, second: Date): boolean {
		return (
			first.getFullYear() === second.getFullYear() &&
			first.getMonth() === second.getMonth() &&
			first.getDate() === second.getDate()
		);
	}

	formatDate(date: string | null): string {
		if (date === null) {
			return "?";
		}
		try {
			return new Date(Date.parse(date)).toLocaleString(this.languageService.getCurrentLanguage());
		} catch (e: unknown) {
			console.error(e);
			return date.toString();
		}
	}
}
